import { z } from "@brains/utils/zod";
import type { BaseEntity, EntityAdapter, EntitySchema } from "../types";
import {
  parseMarkdownWithFrontmatter,
  generateMarkdownWithFrontmatter,
  generateFrontmatter,
} from "../frontmatter";
import { EntityValidationError } from "../errors";

/** Interface for objects that can generate a body template. */
export interface BodyTemplateProvider {
  generateBodyTemplate(): string;
}

const defaultBodyFormatter: BodyTemplateProvider = {
  generateBodyTemplate: () => "",
};

const frontmatterRecordSchema = z.record(z.string(), z.unknown());

export type DefaultEntityFrontmatter<TMetadata extends object> = {
  [K in keyof TMetadata]?: TMetadata[K] | undefined;
};

export type BaseEntityFrontmatterSchema<TFrontmatter> =
  z.ZodObject<z.ZodRawShape> & z.ZodType<TFrontmatter>;

export interface BaseEntityAdapterConfig<
  TEntity extends BaseEntity<TMetadata>,
  TMetadata extends object,
  TFrontmatter = DefaultEntityFrontmatter<TMetadata>,
> {
  entityType: string;
  purpose: string;
  schema: EntitySchema<TEntity>;
  frontmatterSchema: BaseEntityFrontmatterSchema<TFrontmatter>;
  isSingleton?: boolean;
  hasBody?: boolean;
  supportsCoverImage?: boolean;
  bodyFormatter?: BodyTemplateProvider;
}

/**
 * Abstract base class for entity adapters.
 *
 * Provides default implementations for the 3 boilerplate methods
 * (extractMetadata, parseFrontMatter, generateFrontMatter) and
 * protected helpers for common patterns in toMarkdown/fromMarkdown.
 *
 * Subclasses must implement toMarkdown() and fromMarkdown().
 */
export abstract class BaseEntityAdapter<
  TEntity extends BaseEntity<TMetadata>,
  TMetadata extends object = Record<string, unknown>,
  TFrontmatter = DefaultEntityFrontmatter<TMetadata>,
> implements EntityAdapter<TEntity, TMetadata> {
  public readonly entityType: string;
  public readonly purpose: string;
  public readonly schema: EntitySchema<TEntity>;
  public readonly frontmatterSchema: z.ZodObject<z.ZodRawShape>;
  public readonly isSingleton?: boolean;
  public readonly hasBody?: boolean;
  public readonly supportsCoverImage?: boolean;

  // Stored separately with output type preserved for type-safe parsing.
  private readonly fmSchema: z.ZodType<TFrontmatter>;
  private readonly bodyFormatter: BodyTemplateProvider;

  constructor(
    config: BaseEntityAdapterConfig<TEntity, TMetadata, TFrontmatter>,
  ) {
    this.entityType = config.entityType;
    this.purpose = config.purpose;
    this.schema = config.schema;
    this.frontmatterSchema = config.frontmatterSchema;
    this.fmSchema = config.frontmatterSchema;
    this.bodyFormatter = config.bodyFormatter ?? defaultBodyFormatter;
    if (config.isSingleton !== undefined) this.isSingleton = config.isSingleton;
    if (config.hasBody !== undefined) this.hasBody = config.hasBody;
    if (config.supportsCoverImage !== undefined)
      this.supportsCoverImage = config.supportsCoverImage;
  }

  // ── Abstract methods (subclasses must implement) ──

  public abstract fromMarkdown(markdown: string): Partial<TEntity>;

  // ── Default implementations (can be overridden) ──

  /**
   * Serialize an entity to markdown.
   *
   * Rebuilds frontmatter from entity.metadata overlaid on any existing
   * frontmatter parsed from entity.content. Body comes from renderBody,
   * which defaults to the body portion of entity.content (frontmatter
   * stripped).
   *
   * Adapters that want the body to reflect typed fields (e.g. a
   * structured about/skills section) override renderBody. Adapters that
   * need entirely custom serialization override toMarkdown.
   */
  public toMarkdown(entity: TEntity): string {
    const body = this.renderBody(entity);
    const existing = this.readExistingFrontmatter(entity.content);
    const frontmatter: Record<string, unknown> = { ...existing };
    const schemaKeys = Object.keys(this.frontmatterSchema.shape);
    for (const [key, value] of Object.entries(entity.metadata)) {
      if (schemaKeys.includes(key)) frontmatter[key] = value;
    }
    return this.buildMarkdown(body, frontmatter);
  }

  /**
   * Render the body section of the entity.
   *
   * Default returns the body portion of entity.content (frontmatter
   * stripped). Override when the body should be rebuilt from typed
   * fields on the entity.
   */
  protected renderBody(entity: TEntity): string {
    return this.extractBody(entity.content);
  }

  private readExistingFrontmatter(content: string): Record<string, unknown> {
    try {
      return parseMarkdownWithFrontmatter(content, frontmatterRecordSchema)
        .metadata;
    } catch {
      return {};
    }
  }

  public extractMetadata(entity: TEntity): TMetadata {
    return entity.metadata;
  }

  public parseFrontMatter<T>(
    markdown: string,
    schema: { parse(data: unknown): T },
  ): T {
    try {
      return parseMarkdownWithFrontmatter(markdown, schema).metadata;
    } catch (error) {
      throw new EntityValidationError(this.entityType, error);
    }
  }

  public getBodyTemplate(): string {
    return this.bodyFormatter.generateBodyTemplate();
  }

  public generateFrontMatter(entity: TEntity): string {
    return generateFrontmatter(frontmatterRecordSchema.parse(entity.metadata));
  }

  // ── Protected helpers for use in toMarkdown/fromMarkdown ──

  /** Strip frontmatter and return the body content. */
  protected extractBody(markdown: string): string {
    try {
      return parseMarkdownWithFrontmatter(markdown, frontmatterRecordSchema)
        .content;
    } catch {
      return markdown;
    }
  }

  /** Parse frontmatter using this adapter's frontmatter schema. */
  protected parseFrontmatter(markdown: string): TFrontmatter {
    try {
      return parseMarkdownWithFrontmatter(markdown, this.fmSchema).metadata;
    } catch (error) {
      throw new EntityValidationError(this.entityType, error);
    }
  }

  /** Combine body and frontmatter into a markdown string. */
  protected buildMarkdown(
    body: string,
    frontmatter: Record<string, unknown>,
  ): string {
    return generateMarkdownWithFrontmatter(body, frontmatter);
  }
}
